import React, { useEffect, useState } from "react"
import { makeStyles } from "tss-react/mui"
import {
    Box,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    Typography,
} from "@mui/material"
import { useFormikContext } from "formik"
import { StandardMainPanel } from "../../../../../../../../shared/components/forms/StandardMainPanel"
import { CreateRoomResourceForm } from "../../../resourcesTypes"
import { StandardForm } from "../../../../../../../../shared/components/forms/StandardForm"
import { FormikSwitch } from "../../../../../../../../shared/components/forms/Formik"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrash, faXmark } from "@fortawesome/pro-solid-svg-icons"
import { StandardButton } from "../../../../../../../../shared/components/buttons/StandardButton"
import { tailwindPalette } from "../../../../../../../theme/tailwindPalette"
import { LocalizedStrict } from "../../../../../../../localization/components/AppLocalized"

const useStyles = makeStyles()(() => ({
    input: {
        width: "15rem",
    },
    button: {
        padding: ".75rem 2rem !important",
        backgroundColor: tailwindPalette.sky[500],
    },
    listItem: { display: "flex", alignItems: "center" },
}))

/*todo: fetch data from API?*/
const departments = [
    {
        id: 1,
        name: "Accounting",
    },
    {
        id: 2,
        name: "Marketing",
    },
    {
        id: 3,
        name: "Management",
    },
    {
        id: 4,
        name: "Sales",
    },
]

export function AccessControl() {
    const { classes } = useStyles()
    const { setFieldValue, values } = useFormikContext<CreateRoomResourceForm>()

    const [inputValue, setInputValue] = useState<string>("")
    const [selectedDepartments, setSelectedDepartments] = useState<number[]>([])

    useEffect(() => {
        if (values.accessControl.departmentIds) {
            setSelectedDepartments(values.accessControl.departmentIds)
        }
    }, [values.accessControl.departmentIds])

    const handleAddDepartment = () => {
        const department = departments.find((d) => d.name === inputValue)
        if (department && !selectedDepartments.includes(department.id)) {
            const newDepartments = [...selectedDepartments, department.id]
            setSelectedDepartments(newDepartments)
            setFieldValue("accessControl.departmentIds", newDepartments)
            setInputValue("")
        }
    }

    const handleDeleteDepartment = (id: number) => {
        const newDepartments = selectedDepartments.filter((d) => d !== id)
        setSelectedDepartments(newDepartments)
        setFieldValue("accessControl.departmentIds", newDepartments)
    }

    const handleClearInput = () => {
        setInputValue("")
    }

    const handleSelectChange = (event: SelectChangeEvent<string>) => {
        setInputValue(event.target.value)
    }

    const availableSuggestions = departments.filter((d) => !selectedDepartments.includes(d.id)).map((d) => d.name)

    return (
        <StandardForm>
            <LocalizedStrict id="tab-access-control-restricted-access-title" attrs={{ title: true }}>
                <StandardMainPanel
                    variant="switchable"
                    checked={values.accessControl.limitAccess}
                    onCheckChange={() => {
                        setFieldValue("accessControl.limitAccess", !values.accessControl.limitAccess)
                    }}
                    title="Restricted access"
                >
                    <Box className={classes.input}>
                        <LocalizedStrict id="tab-access-control-visible-everyone-switch">
                            <FormikSwitch labelPlacement="end" name="accessControl.visibleForAll">
                                Visible for everyone
                            </FormikSwitch>
                        </LocalizedStrict>
                    </Box>

                    <LocalizedStrict id="tab-access-control-reserved-for-company-title">
                        <Typography variant="body1">Reserved for company:</Typography>
                    </LocalizedStrict>
                    {/*todo: display list form API?*/}
                    <Typography variant="subtitle1">Wonder Media Inc</Typography>

                    <LocalizedStrict id="tab-access-control-bookable-by-departments-title">
                        <Typography variant="body1">Bookable by departments:</Typography>
                    </LocalizedStrict>

                    {selectedDepartments.length > 0 && (
                        <List>
                            {selectedDepartments.map((id) => {
                                const department = departments.find((d) => d.id === id)
                                return (
                                    <ListItem key={id} className={classes.listItem}>
                                        <ListItemText primary={department?.name} />
                                        <IconButton edge="end" onClick={() => handleDeleteDepartment(id)} size="small">
                                            <FontAwesomeIcon icon={faTrash} />
                                        </IconButton>
                                    </ListItem>
                                )
                            })}
                        </List>
                    )}

                    <Stack direction="row" spacing=".5rem" alignItems="center">
                        <Select
                            value={inputValue}
                            onChange={handleSelectChange}
                            size="small"
                            className={classes.input}
                            endAdornment={
                                inputValue && (
                                    <InputAdornment sx={{ marginRight: "1rem" }} position="end">
                                        <IconButton size="small" onClick={handleClearInput}>
                                            <FontAwesomeIcon icon={faXmark} />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }
                        >
                            <MenuItem value="" disabled>
                                <LocalizedStrict id="tab-access-control-select-department-select-item">
                                    <em>Select a department</em>
                                </LocalizedStrict>
                            </MenuItem>
                            {availableSuggestions.map((suggestion) => (
                                <MenuItem key={suggestion} value={suggestion}>
                                    {suggestion}
                                </MenuItem>
                            ))}
                        </Select>
                        <LocalizedStrict id="tab-access-control-add-button">
                            <StandardButton
                                fullWidth={false}
                                className={classes.button}
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={handleAddDepartment}
                            >
                                Add
                            </StandardButton>
                        </LocalizedStrict>
                    </Stack>
                </StandardMainPanel>
            </LocalizedStrict>
        </StandardForm>
    )
}

